import { IconPencil, IconPrinter, IconQrcode, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { RowActions } from 'src/Components/Shared/RowActions';
import { useCreateInstructionExport } from 'src/Hooks/Mutations/useCreateInstructionExport';
import { useDeleteInstruction } from 'src/Hooks/Mutations/useDeleteInstruction';
import usePrintInstruction from 'src/Hooks/Printables/usePrintInstruction';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { useCanCreate, useItemPermissions } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import { usePermissionDeniedModal } from 'src/Hooks/Shared/usePermissionDeniedModal';

import { Props } from './InstructionMenu.types';

const InstructionMenu = ({ onDelete, instruction }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { mutate: exportInstruction } = useCreateInstructionExport({
    onSuccess: data => {
      setExportUrl(data.publicUrl);
      setShowQRCodeModal(true);
    },
  });
  const { mutate: exportForPrint, isLoading: isPrinting } = useCreateInstructionExport({
    onSuccess: data => {
      printInstruction({ instruction, assetName: asset?.name ?? '', exportUrl: data.publicUrl });
    },
  });
  const { mutate: deleteInstruction } = useDeleteInstruction({
    onMutate: () => {
      onDelete();
      return undefined;
    },
  });

  const { data: asset } = useFetchAssetDetails();
  const { printInstruction } = usePrintInstruction();
  const assetAboveQuotas = useIsAssetAboveQuotas();
  const { canEdit, canDelete, ownerName } = useItemPermissions(
    'instruction',
    instruction.createdBy
  );
  const { hasPermission: canExport } = useCanCreate('entityExport');
  const showPermissionDeniedModal = usePermissionDeniedModal(ownerName);

  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [exportUrl, setExportUrl] = useState<string | undefined>(undefined);
  const [exportName, setExportName] = useState<string | undefined>(undefined);
  return (
    <>
      <>
        <QRCodeModal
          open={showQRCodeModal}
          url={exportUrl ?? ''}
          onClose={() => setShowQRCodeModal(false)}
          exportName={exportName ?? ''}
        />
        <RowActions
          actions={[
            {
              key: 'edit',
              label: t('instructions.menu.edit', 'Modifier'),
              icon: <IconPencil size={18} />,
              disabled: assetAboveQuotas,
              onClick: () => {
                if (!canEdit) {
                  showPermissionDeniedModal();
                  return;
                }
                navigate('edit', { state: { disableAnimation: true }, replace: true });
              },
            },
            {
              key: 'export',
              label: t('instructions.menu.export', 'Exporter'),
              icon: <IconQrcode size={18} />,
              hidden: !canExport,
              onClick: () => {
                setExportName(instruction.title);
                exportInstruction([instruction.id]);
              },
            },
            {
              key: 'print',
              label: t('instructions.menu.print', 'Imprimer la consigne'),
              icon: <IconPrinter size={18} />,
              disabled: isPrinting,
              onClick: () => exportForPrint([instruction.id]),
            },
            {
              key: 'delete',
              label: t('instructions.menu.delete', 'Supprimer'),
              icon: <IconTrash size={18} />,
              variant: 'danger',
              onClick: () => {
                if (!canDelete) {
                  showPermissionDeniedModal();
                  return;
                }
                deleteInstruction(instruction.id);
              },
            },
          ]}
        />
      </>
    </>
  );
};

export default InstructionMenu;
